import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { Components } from '@mui/material/styles';
import { type Theme } from '@mui/material/styles';
import { faChevronDown } from '@rtkwlf/pro-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@rtkwlf/pro-solid-svg-icons/faChevronUp';
import { faFilter } from '@rtkwlf/pro-solid-svg-icons/faFilter';

import { GridColumnsManagementDefault } from '../columns-management/grid-columns-management-default';
import { getTogglableColumnsDefault } from '../columns-management/init-togglable-columns/init-togglable-columns';

import { filterFormPropsDefaults } from './defaults';

export const datagridTheme: Pick<Components<Theme>, 'MuiDataGrid'> = {
  MuiDataGrid: {
    defaultProps: {
      slots: {
        // updating icon to align with other filter icons in the table
        openFilterButtonIcon: () => <FontAwesomeIcon icon={faFilter} />,
        // updating MUI plus icon to FontAwesome caret
        detailPanelExpandIcon: () => <FontAwesomeIcon icon={faChevronDown} />,
        // updating MUI plus icon to FontAwesome caret
        detailPanelCollapseIcon: () => <FontAwesomeIcon icon={faChevronUp} />,
        columnsManagement: GridColumnsManagementDefault,
      },
      slotProps: {
        baseTextField: {
          // update field to use outlined textfield
          variant: 'outlined',
          size: 'small',
        },
        columnsManagement: {
          getTogglableColumns: getTogglableColumnsDefault,
        },
        filterPanel: {
          // update fields to use outlined textfield
          filterFormProps: filterFormPropsDefaults,
        },
        toolbar: {},
        panel: {
          placement: 'bottom-end', // change the placement of panel to reflect the order change of the custom toolbar (grid-toolbar.tsx)
        },
      },
    },

    styleOverrides: {
      root: {
        border: 'none', // remove border surrounding table

        // Aligning gridPanelAnchor on the right to reflect the order change of the custom toolbar (grid-toolbar.tsx)
        '[data-id="gridPanelAnchor"]': {
          right: 0,
          left: 'unset',
        },
      },

      // The density dropdown works using line-heights to make the cell larger
      // When getRowHeight is set to auto, the line-height becomes inherit and the cell size doesn't change
      // Add a padding when dynamic height is enable for the different density options
      'root--densityCompact': ({ theme }) => ({
        '.MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': {
          paddingTop: theme.spacing(1 / 2),
          paddingBottom: theme.spacing(1 / 2),
        },
      }),
      'root--densityStandard': ({ theme }) => ({
        '.MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': {
          paddingTop: theme.spacing(1),
          paddingBottom: theme.spacing(1),
        },
      }),
      'root--densityComfortable': ({ theme }) => ({
        '.MuiDataGrid-row--dynamicHeight > .MuiDataGrid-cell': {
          paddingTop: theme.spacing(2),
          paddingBottom: theme.spacing(2),
        },
      }),
      // Vertically align details panel toggle button when row height is set to auto
      'row--dynamicHeight': {
        '> .MuiDataGrid-cell[data-field="__detail_panel_toggle__"]': {
          '&[data-field="__detail_panel_toggle__"]': {
            alignItems: 'center',
            display: 'flex',
          },
        },
      },
      // toolbar overrides
      toolbarContainer: {
        '.svg-inline--fa': {
          fontSize: '0.85rem', // match icon size of the other toolbar items
        },
      },
      // filter panel overrides
      panelContent: {
        // add spacing around outlined textfield
        padding: '8px',
      },
      filterForm: {
        // add gap between outlined textfield
        gap: '8px',
      },
      filterFormDeleteIcon: ({ theme }) => ({
        justifyContent: 'unset', // remove style so every form item aligns from the top
        marginTop: theme.spacing(1 / 2), // align button with input fields
      }),
      filterFormLogicOperatorInput: {
        justifyContent: 'unset', // remove style so every form item aligns from the top
      },
      filterFormOperatorInput: {
        // need to explicitly set the width
        width: '135px',
      },
      filterFormValueInput: ({ theme }) => ({
        // need to explicitly set the width
        width: '275px',
        [theme.breakpoints.up('lg')]: {
          width: '400px',
        },
      }),
      // expanded row overrides
      detailPanelToggleCell: {
        '.svg-inline--fa': {
          fontSize: '0.85rem', // match icon size for toggle cell
        },
      },
      // tree data overrides
      treeDataGroupingCellToggle: {
        alignContent: 'center',
      },
      cell: {
        // tree data expended row overrides
        '&[data-field="__tree_data_group__"]': {
          display: 'flex',
        },

        // cell customization: class to apply when a row has a fixed height but cell has content that could overflows
        '&.overflowY': {
          overflowY: 'scroll',

          '> *': {
            alignContent: 'center',
            height: '100%',
          },
        },
      },
    },
  },
};
